/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable @typescript-eslint/prefer-optional-chain */
/* eslint-disable no-duplicate-imports */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Table,
  Popover,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
  Avatar,
  Grid,
  Drawer,
  IconButton,
  Button,
  InputAdornment,
  Divider
} from '@mui/material';
import { AddLevelIcon, CircleAddIcon, PlusCircleIcon } from '../../../../../utils/Icons';
import SearchIcon from '@mui/icons-material/Search';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { API } from '../../../../../api';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import AddProgram from '../Dialogs/AddProgram';
import ProgramTableRow from './ProgramTableRow';
// import FilterModal from '../../../components/filter/FilterModal';
import { groupActions } from '../../../../../redux/slices/getAllGroups/getAllGroupsSlice';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { fetchAllGroupsList } from '../../../../../redux/slices/getAllGroupsListSlice';
import orgName, { getUserDetails } from '../../../../../utils/orgName';
import ProgramTableLoader from '../../../../../Loader/ProgramTableLoader';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { groupLoaderActions } from '../../../../../redux/slices/groupLoaderSlice';
import ROLES from '../../../../../utils/roles';
import { AGILE_ORG } from '../../../../../utils/constants';
import _ from 'lodash';
import useMenteePeers from '../../../../../queries/useMenteePeers';
import DosenInput from '../../../components/DosenInput';
import MySkeleton from '../../../../../components/Skeleton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';

interface GroupInterface {
  grpId: string;
  name: string;
  status: string;
  type: string;
  orgId: string;
  creatorName?: string;
  avatar?: string;
  typeLabel?: string;
  description?: string;
  buildState?: Record<string, any>;
  creator?: {
    id?: string;
    name?: string;
    headshot?: string;
  };
  createdBy?: string;
}

interface ProgramCardProps extends GroupInterface {
  onClick?: () => void;
  selected?: boolean;
  description?: string;
  avatar?: string;
  goals?: any;
}

interface AdminProgramItem {
  id: string;
  name: string;
  type: string;
  status: string;
  createdBy?: string;
  creator?: {
    id?: string;
    name?: string;
    headshot?: string;
  };
  buildState?: {
    description?: string;
  };
}

interface UserProgramInfo {
  id: string;
  name: string;
  type: string;
  roles: string[];
  progress?: number;
  etaText?: string;
  status?: string;
  objectivesPending?: number;
  menteesEngaged?: number;
  messagesSent?: number;
  meetingsScheduled?: number;
  onClick?: () => void;
  loading?: boolean;
  orgId?: string;
  grpId?: string;
  buildState?: Record<string, any>;
}

type ColumnStatus = 'design' | 'published' | 'invited' | 'active';

const STATUS_COLUMNS: { key: ColumnStatus; label: string }[] = [
  { key: 'design', label: 'Design' },
  { key: 'published', label: 'Published' },
  { key: 'invited', label: 'Invited' },
  { key: 'active', label: 'Active' }
];

const DEFAULT_COLUMN_STATUS: ColumnStatus = 'design';
const STATUS_COLUMN_MAP: Record<string, ColumnStatus> = {
  design: 'design',
  designed: 'design',
  published: 'published',
  invited: 'invited',
  actived: 'active',
  active: 'active'
};

const isDesignedProgram = (status?: string) => (status || '').toLowerCase() === 'designed';

type ResumeBuildState = {
  programType?: 'masterclass' | 'guided-masterclass' | 'mentorship';
  buildMode?: 'ai' | 'manual';
  ai?: {
    purpose?: { name?: string };
    objectives?: string[];
    selectedDocIds?: string[];
    documents?: any[];
    content?: any;
  };
  mentorship?: {
    name?: string;
    learningObjectives?: string[];
  };
  generationId?: string;
  groupId?: string;
};

const normalizePersistentBuildState = (state: Record<string, any>) => {
  const aiState = state.ai || {};
  return {
    programType: state.programType || 'masterclass',
    masterclass: state.masterclass || {},
    mentorship: state.mentorship || {},
    guidedMasterclass: state.guidedMasterclass || {},
    buildMode: state.buildMode || (aiState ? 'ai' : 'manual'),
    ai: {
      step: aiState.step ?? 1,
      purpose: aiState.purpose || {},
      objectives: aiState.objectives || [],
      reviewData: aiState.reviewData ?? null,
      members: aiState.members || [],
      documents: aiState.documents || [],
      pendingDocs: aiState.pendingDocs || [],
      selectedDocIds: aiState.selectedDocIds || [],
      structure: aiState.structure || null,
      content: aiState.content ?? null,
      structureLoading: Boolean(aiState.structureLoading),
      contentLoading: Boolean(aiState.contentLoading),
      contentStatus: aiState.contentStatus ?? null,
      contentProgress: aiState.contentProgress ?? null,
      contentPollingActive: Boolean(aiState.contentPollingActive)
    },
    groupId: state.groupId,
    generationId: state.generationId,
    modules:
      state.modules ||
      aiState.structure?.programStructure?.modules ||
      []
  };
};

const persistProgramCreation = (state?: Record<string, any>) => {
  if (!state) return;
  try {
    const normalized = normalizePersistentBuildState(state);
    const snapshot = JSON.stringify(normalized);
    localStorage.setItem('programCreation', snapshot);
  } catch (error) {
    console.error('Failed to persist program creation state', error);
  }
};

const determineAiSegment = (ai?: ResumeBuildState['ai']) => {
  const hasPurpose = Boolean(ai?.purpose?.name?.trim());
  const hasObjectives = (ai?.objectives || []).some((value) => Boolean(value?.trim()));
  const hasDocs =
    (ai?.selectedDocIds && ai.selectedDocIds.length > 0) ||
    (ai?.documents && ai.documents.length > 0);
  const hasContent = Boolean(ai?.content);

  if (!hasPurpose) return 'purpose';
  if (!hasObjectives) return 'objectives';
  if (!hasDocs) return 'documents';
  if (!hasContent) return 'review';
  return 'members';
};

const getManualPrefix = (programType: ResumeBuildState['programType']) =>
  programType === 'guided-masterclass' ? 'guided-masterclass' : 'masterclass';

const getProgramRouteSuffix = (buildState?: ResumeBuildState) => {
  if (!buildState) return '/start';
  const baseType = buildState.programType || 'masterclass';
  if (baseType === 'mentorship') {
    const hasName = Boolean(buildState.mentorship?.name?.trim());
    const hasObjectives = (buildState.mentorship?.learningObjectives?.length ?? 0) > 0;
    if (!hasName) return '/mentorship/program-name';
    if (!hasObjectives) return '/mentorship/learning-objectives';
    return '/mentorship/members';
  }

  const prefix = baseType === 'guided-masterclass' ? 'guided-masterclass' : 'masterclass';
  if (!buildState.buildMode) {
    return `/${prefix}/step-2`;
  }
  if (buildState.buildMode === 'manual') {
    return `/${prefix}/manual`;
  }
  if (buildState.buildMode === 'ai') {
    const segment = determineAiSegment(buildState.ai);
    return `/${prefix}/ai/${segment}`;
  }
  return `/${prefix}/step-2`;
};

interface ResumeProgramArgs {
  program: {
    buildState?: ResumeBuildState;
    orgId?: string;
    grpId?: string;
    id?: string;
    status?: string;
  };
  orgId?: string;
  coId?: string;
  navigate: ReturnType<typeof useNavigate>;
}

const resumeProgramCreationFlow = async ({ program, orgId, coId, navigate }: ResumeProgramArgs) => {
  const buildState = program.buildState;
  if (!buildState || !buildState.groupId) return false;

  const ensureGeneration = async (stateToEnsure: ResumeBuildState) => {
    if (!stateToEnsure.programType?.includes('masterclass')) return stateToEnsure;
    if (stateToEnsure.generationId) {
      try {
        await API.resumeProgramId({ generationId: stateToEnsure.generationId });
      } catch (error) {
        console.warn('Failed to resume generation, will request a new one', error);
        stateToEnsure.generationId = undefined;
      }
    }
    if (!stateToEnsure.generationId && stateToEnsure.groupId) {
      try {
        const response = await API.generateProgramId({ groupId: stateToEnsure.groupId });
        stateToEnsure.generationId = response?.data?.id;
      } catch (error) {
        console.error('Failed to generate program id', error);
      }
    }
    return stateToEnsure;
  };

  const updatedState = (await ensureGeneration({ ...buildState })) as ResumeBuildState;
  persistProgramCreation(updatedState);

  const orgIdParam = orgId || program.orgId;
  const coIdParam = coId || program.orgId || orgIdParam;
  const programId = program.grpId || program.id || updatedState.groupId;
  if (!orgIdParam || !coIdParam || !programId) return false;
  const suffix = getProgramRouteSuffix(updatedState);
  navigate(`/app/programdetails/${orgIdParam}/${coIdParam}/admin/new-program${suffix}`, {
    replace: true
  });
  return true;
};

const TYPE_LABEL_OVERRIDES: Record<string, string> = {
  'masterclass&mentorship': 'Guided Masterclass',
  masterclass: 'Masterclass',
  mentorship: 'Mentorship'
};

const BADGE_STYLES: Record<string, { background: string; color: string }> = {
  Mentorship: { background: '#8E44FF', color: '#FFFFFF' },
  Masterclass: { background: '#F0B80D', color: '#0F0F0F' },
  'Guided Masterclass': { background: '#F15A26', color: '#FFFFFF' }
};

const inferTypeFromGoals = (goals: any) => {
  if (!goals) return 'mentorship';
  if (typeof goals === 'object' && Object.keys(goals).length === 0) {
    return 'mentorship';
  }
  return 'masterclass';
};

const getProgramTypeLabel = (type?: string, goals?: any) => {
  if (type) {
    return TYPE_LABEL_OVERRIDES[type] || _.startCase(type);
  }
  const fallback = inferTypeFromGoals(goals);
  return fallback === 'masterclass' ? 'Masterclass' : 'Mentorship';
};

const getBadgeStyle = (label: string) => BADGE_STYLES[label] || { background: '#F0B80D', color: '#0F0F0F' };

const getColumnStatus = (status?: string): ColumnStatus => {
  const normalized = (status || '').toLowerCase();
  return STATUS_COLUMN_MAP[normalized] || DEFAULT_COLUMN_STATUS;
};

function Sidebar({
  open,
  onClose,
  program,
  onManage,
  canManageProgram
}: {
  open: boolean;
  onClose: () => void;
  program?: GroupInterface;
  onManage?: (program: GroupInterface) => Promise<boolean> | boolean;
  canManageProgram?: boolean;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || '';
  const orgId = useParams().orgId || '';
  let param = '';
  if (role === ROLES.platAdmin) {
    param = `/?org=${orgName}`;
  }
  const badgeText = program?.typeLabel || getProgramTypeLabel(program?.type);
  const creatorDisplay = program?.creatorName || 'Program admin';
  const creatorAvatar = program?.avatar || 'https://placehold.co/25x25';
  const isManageDisabled = !program || !canManageProgram;
  const handleManageClick = async () => {
    if (!program || isManageDisabled) return;
    if (await onManage?.(program)) {
      return;
    }
    dispatch(groupLoaderActions.updateState(true));
    navigate(`/app/programdetails/${orgId}/${program?.orgId}/${program?.grpId}/settings`);
  };
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 375,
          background: '#151515',
          borderLeft: '1px solid #2A2A2A'
        }
      }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* HEADER TAG + CLOSE */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            background: getBadgeStyle(badgeText).background,
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            sx={{
              fontSize: 9,
              fontFamily: '"Open Sans", sans-serif',
              fontWeight: 700,
              color: getBadgeStyle(badgeText).color,
              lineHeight: '9px'
            }}
          >
            {badgeText}
          </Typography>
        </Box>

          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ color: '#FDFDFD' }} />
          </IconButton>
        </Box>

        {/* TITLE + DESCRIPTION */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            sx={{
              color: '#FDFDFD',
              fontSize: 26,
              fontWeight: 600,
              textTransform: 'capitalize',
              lineHeight: '31.2px'
            }}
          >
            {program?.name}
          </Typography>

          <Typography
            sx={{
              color: '#B6B6B6',
              fontSize: 14,
              fontWeight: 400,
              lineHeight: '16.8px'
            }}
          >
            {/* Description in future comes here */}
          </Typography>
        </Box>

        {/* MENTOR */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src={creatorAvatar} sx={{ width: 25, height: 25 }} />
          <Typography
            sx={{
              color: '#FDFDFD',
              fontSize: 10,
              fontFamily: 'Inter',
              fontWeight: 700,
              lineHeight: '12px'
            }}
          >
            by {creatorDisplay}
          </Typography>
        </Box>

        {/* DIVIDER */}
        <Box sx={{ width: '100%', height: '1px', background: '#444444' }} />

        {/* BUTTON */}
        <Box
          sx={{
            width: '100%',
            py: 1.3,
            px: 2,
            background: '#1C1C1C',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            transition: '0.2s',
            cursor: isManageDisabled ? 'not-allowed' : 'pointer',
            opacity: isManageDisabled ? 0.6 : 1,
            '&:hover': {
              background: isManageDisabled ? '#1C1C1C' : '#2A2A2A'
            }
          }}
          onClick={handleManageClick}
        >
          {isManageDisabled && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '6px',
                background: 'rgba(0, 0, 0, 0.45)',
                zIndex: 1,
                pointerEvents: 'none'
              }}
            />
          )}
          <Typography
            sx={{
              color: '#FDFDFD',
              fontSize: 13,
              fontWeight: 500,
              textAlign: 'center',
              lineHeight: '19.5px',
              position: 'relative',
              zIndex: 2
            }}
          >
            Manage Program
          </Typography>
          {isManageDisabled && (
            <LockOutlinedIcon
              sx={{
                position: 'absolute',
                top: '50%',
                right: 14,
                transform: 'translateY(-50%)',
                color: '#FDFDFD',
                opacity: 0.85,
                fontSize: 18,
                zIndex: 3,
                pointerEvents: 'none'
              }}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

function UserProgramCard({ program }: { program: UserProgramInfo }) {
  const primaryRole = program.roles.join(' / ');
  const hasMentorRole = program.roles.some((role) => role.toLowerCase().includes('mentor'));
  const { orgId, location } = getUserDetails();
  const { data: menteePeersData } = useMenteePeers({ grpId: program.id, orgId, location });
  const peerProgress = menteePeersData?.data?.data?.[0]?.avgGoalProgress;
  const progress =
    typeof peerProgress === 'number'
      ? peerProgress
      : typeof program.progress === 'number'
      ? program.progress
      : undefined;
  const showMentorStats = hasMentorRole;
  const objectivesText = program.objectivesPending
    ? `${program.objectivesPending} objectives yet to be completed`
    : '~ Time info unavailable';

  return (
    <Box
      sx={{
        width: '100%',
        p: 2.5,
        background: '#151515',
        borderRadius: '5px',
        outline: '2px solid #FDFDFD',
        outlineOffset: '-2px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5
      }}
      onClick={program.onClick}
      role={program.onClick ? 'button' : undefined}
      tabIndex={program.onClick ? 0 : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box
            sx={{
              px: 1,
              py: 0.5,
              background: '#F15A26',
              borderRadius: '20px',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <Typography
              sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 400, lineHeight: '14px', fontFamily: 'Inter' }}
            >
              {program.type || 'Program'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 400, fontFamily: 'Inter' }}>Role:</Typography>
            <Box sx={{ overflow: 'hidden', borderRadius: '6px', display: 'flex' }}>
              <Box
                sx={{
                  px: 1,
                  py: 0.7,
                  background: '#444444',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography sx={{ color: '#FDFDFD', fontSize: 10, fontWeight: 700, fontFamily: 'Inter' }}>
                  {primaryRole}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Typography
          sx={{ color: '#FDFDFD', fontSize: 22, fontWeight: 600, textTransform: 'capitalize', fontFamily: 'Inter' }}
        >
          {program.name}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400, fontFamily: 'Inter' }}>
          Program Progress: {progress !== undefined ? `${progress}%` : 'N/A'}
        </Typography>
        <Box sx={{ height: 6, background: '#2A2A2A', borderRadius: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: `${Math.min(Math.max(progress || 0, 0), 100)}%`,
              background: '#F0B80D'
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
        {showMentorStats ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
              {program.menteesEngaged ?? 0} Mentees Engaged
            </Typography>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
              {program.messagesSent ?? 0} Messages Sent
            </Typography>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
              {program.meetingsScheduled ?? 0} Meetings Scheduled
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
            <Box sx={{ width: 16, height: 16, position: 'relative' }}>
              <Box sx={{ position: 'absolute', inset: 0, border: '1.5px solid #868686' }} />
            </Box>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
              {program.etaText || objectivesText}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
            Continue Program
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function UserProgramDrawer({
  open,
  onClose,
  program
}: {
  open: boolean;
  onClose: () => void;
  program?: UserProgramInfo;
}) {
  const { orgId, location } = getUserDetails();
  const { data: menteePeersData } = useMenteePeers({
    grpId: program?.id || '',
    orgId,
    location
  });
  const peerData = menteePeersData?.data?.data?.[0];
  const progress = typeof peerData?.avgGoalProgress === 'number' ? peerData?.avgGoalProgress : program?.progress;

  if (!program) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          background: '#151515',
          borderLeft: '1px solid #2A2A2A',
          p: 3
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            sx={{
              px: 1,
              py: 0.5,
              background: '#F15A26',
              borderRadius: '20px',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 400, fontFamily: 'Inter' }}>
              {program.type}
            </Typography>
          </Box>
          <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
            {program.roles.join(' / ')}
          </Typography>
        </Box>

        <Typography sx={{ color: '#FDFDFD', fontSize: 22, fontWeight: 600, textTransform: 'capitalize' }}>
          {program.name}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400, fontFamily: 'Inter' }}>
            Program Progress: {typeof progress === 'number' ? `${progress}%` : 'N/A'}
          </Typography>
          <Box sx={{ height: 6, background: '#2A2A2A', borderRadius: 3, overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${Math.min(Math.max(progress || 0, 0), 100)}%`,
                background: '#F0B80D'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, fontFamily: 'Inter' }}>Peers</Typography>
          {peerData?.menteeDetails?.length ? (
            peerData.menteeDetails.map((peer: any, idx: number) => (
              <Box
                key={peer.userId || idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  borderBottom: '1px solid #2A2A2A'
                }}
              >
                <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>{peer.displayName}</Typography>
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 600 }}>
                  {peer.goalProgress ?? 0}%
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ color: '#868686', fontSize: 12 }}>No peer data available.</Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export function ProgramCard({
  type,
  name,
  description = '',
  avatar,
  selected = false,
  goals,
  typeLabel,
  creatorName,
  onClick
}: ProgramCardProps) {
  const badgeLabel = typeLabel || getProgramTypeLabel(type, goals);
  const badgeStyle = getBadgeStyle(badgeLabel);
  const displayAvatar = avatar || 'https://placehold.co/25x25';
  const displayCreator = creatorName || 'Program admin';

  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        height: '200px',
        p: '15px',
        background: '#151515',
        borderRadius: '5px',

        border: selected ? '2px solid white' : '2px solid #444444',
        outlineOffset: '-2px',

        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
        boxSizing: 'border-box',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'Inter, sans-serif',
        '& *': { fontFamily: 'Inter, sans-serif !important' },
        transition: '0.2s',
        '&:hover': {
          borderColor: 'white'
        }
      }}
    >
      {/* TAG */}
      <Box
      sx={{
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}
    >
      <Box
        sx={{
          px: '5px',
          py: '3px',
          background: badgeStyle.background,
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '2px'
        }}
      >
        <Typography
          sx={{
            color: badgeStyle.color,
            fontSize: '9px',
            fontWeight: 700,
            fontFamily: '"Open Sans", sans-serif',
            lineHeight: '9px'
          }}
        >
          {badgeLabel}
        </Typography>
      </Box>
    </Box>

      {/* TITLE */}
      <Typography
        sx={{
          alignSelf: 'stretch',
          color: '#FDFDFD',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '16.8px',
          textTransform: 'capitalize'
        }}
      >
        {name}
      </Typography>

      {/* DESCRIPTION */}
      {/* <Typography
        sx={{
          alignSelf: 'stretch',
          color: '#B6B6B6',
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '14.4px'
        }}
      >
        {description ||
          'A guided challenge blending structured learning and mentoring to close performance gaps with speed and clarity.'}
      </Typography> */}

      {/* DIVIDER */}
      <Box
        sx={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          height: '1px'
        }}
      >
        <Box
          sx={{
            flex: '1 1 0',
            width: '100%',
            background: '#444444'
          }}
        />
      </Box>

      {/* CREATOR */}
      <Box
        sx={{
          alignSelf: 'stretch',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Avatar
          src={displayAvatar}
          sx={{
            width: 25,
            height: 25,
            borderRadius: '9999px'
          }}
        />
        <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500 }}>{displayCreator}</Typography>
      </Box>
    </Box>
  );
}

function ProgramsSidebar() {
  const { id: paramId = '', coId = '', orgId: paramOrgId = '' } = useParams();
  const navigate = useNavigate();
  const orgId = paramOrgId || getUserDetails().orgId;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        background: '#151515',
        borderRight: '1px solid #2A2A2A',
        display: 'flex',
        flexDirection: 'column',
        gap: '30px',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: '25px',
          py: '20px',
          pr: '45px',
          display: 'flex',
          alignItems: 'center',
          gap: '20px'
        }}
      >
        <Typography
          sx={{
            flex: 1,
            color: '#F79420',
            fontSize: 18,
            fontWeight: 700,
            fontFamily: "Inter"
          }}
        >
          Programs Dashboard
        </Typography>
      </Box>

      {/* Description */}
      <Box sx={{ px: '25px', py: '15px', display: 'flex' }}>
        <Typography
          sx={{
            color: '#B6B6B6',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '20px',
            fontFamily: 'Inter'
          }}
        >
          Welcome to your Program Dashboard where you can launch new training programs and track those currently being
          prepared for launch.
          <br />
          <br />
          Here to help you every step of the way, Elliott is your AI-powered program creator, designed to help you build
          structured, high-quality training programs with speed and clarity.
        </Typography>
      </Box>

      {/* CTA */}
      <Box sx={{ px: '25px', py: '15px' }}>
        <DosenButton
          fullWidth
          sx={{
            py: '10px',
            px: '14px',
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
            borderRadius: '6px',
            color: '#FDFDFD',
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(180deg, #F9A53E 0%, #F06839 100%)'
            }
          }}
          onClick={() => navigate(`/app/programdetails/${orgId}/${coId}/${paramId}/new-program/start`)}
        >
          Launch New Program
        </DosenButton>
      </Box>
    </Box>
  );
}

const AdminProgramDashboard = () => {
  const allGroupData = useAppSelector((state) => state.allGroups.data);
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  const groupRoles = useAppSelector((state) => state.groupRoleList.data) || [];
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);
  const orgRealId = useAppSelector((state) => state.orgDetails.data?._id);
  const [userPrograms, setUserPrograms] = useState<UserProgramInfo[]>([]);
  const [userProgramsLoading, setUserProgramsLoading] = useState(true);
  const { orgId, coId, location, userId } = getUserDetails();
  const userValidationId = useAppSelector((state) => state.userProfile.data?._id);

  const [openSidebar, setOpenSidebar] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<GroupInterface | undefined>();
  const [openUserDrawer, setOpenUserDrawer] = useState(false);
  const [selectedUserProgram, setSelectedUserProgram] = useState<UserProgramInfo | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const { id: paramId = '' } = useParams();
  const isAdminRoute = paramId === 'admin';
  const isAdminView = isAdminRoute || selectedGroup.role === 'admin';
  const navigate = useNavigate();
  const adminGroupIds = useMemo(
    () => groupRoles.filter((each) => each.role === 'admin').map((each) => each.id),
    [groupRoles]
  );
  const canManageSelectedProgram = useMemo(() => {
    if (!selectedProgram) return false;
    const programId = selectedProgram.grpId;
    const isAdmin = Boolean(programId && adminGroupIds.includes(programId));
    const creatorId = selectedProgram.creator?.id || selectedProgram.createdBy;
    const isCreator = Boolean(creatorId && userValidationId && creatorId === userValidationId);
    return isAdmin || isCreator;
  }, [selectedProgram, adminGroupIds, userValidationId]);

  const storedTokens = (() => {
    if (typeof window === 'undefined') return {};
    const tokenString = localStorage.getItem('tokens');
    return tokenString ? JSON.parse(tokenString) : {};
  })();
  const idToken = storedTokens?.id_token;
  const accessToken = storedTokens?.access_token;
  const effectiveOrgId = orgRealId || orgId;
  const [adminPrograms, setAdminPrograms] = useState<GroupInterface[]>([]);
  const [adminProgramsLoading, setAdminProgramsLoading] = useState(false);
  const [adminProgramsError, setAdminProgramsError] = useState<string | null>(null);

  const handleSidebarResume = async (program?: GroupInterface) => {
    if (!program) return false;
    return isDesignedProgram(program.status) && (await resumeProgramCreationFlow({ program, orgId, coId, navigate }));
  };

  const fetchAdminPrograms = useCallback(async () => {
    if (!effectiveOrgId || !idToken || !accessToken) {
      setAdminPrograms([]);
      setAdminProgramsLoading(false);
      return;
    }

    setAdminProgramsLoading(true);
    setAdminProgramsError(null);
    try {
      const response = await API.getAdminProgramList({
        orgId: effectiveOrgId,
        idtoken: idToken,
        authorization: accessToken
      });
      const items: AdminProgramItem[] = response?.data?.data || [];
      const mappedPrograms = items.map((item) => ({
        grpId: item.id,
        name: item.name,
        status: item.status,
        type: item.type,
        orgId: effectiveOrgId,
        creatorName: item.creator?.name || item.createdBy || 'Program admin',
        avatar: item.creator?.headshot || '',
        typeLabel: getProgramTypeLabel(item.type),
        description: item.buildState?.description,
        buildState: item.buildState,
        creator: item.creator,
        createdBy: item.createdBy
      }));
      setAdminPrograms(mappedPrograms);
    } catch (error) {
      console.error('Failed to load admin programs', error);
      setAdminPrograms([]);
      setAdminProgramsError('Unable to load programs');
    } finally {
      setAdminProgramsLoading(false);
    }
  }, [effectiveOrgId, idToken, accessToken]);

  useEffect(() => {
    if (!isAdminView) {
      setAdminPrograms([]);
      setAdminProgramsLoading(false);
      return;
    }
    fetchAdminPrograms();
  }, [fetchAdminPrograms, isAdminView]);

  useEffect(() => {
    if (selectedProgram) setOpenSidebar(true);
  }, [selectedProgram]);

  const userVisibleGroupIds = useMemo(() => {
    if (isAdminView) {
      return allGroupData?.groups?.map((group) => group.grpId);
    }
    const allowedRoles = groupRoles.filter((each) => {
      if (each.role === 'admin') return false;
      if (each.role === 'mentee' && oid === AGILE_ORG) return false;
      return true;
    });
    return allowedRoles.map((each) => each.id);
  }, [allGroupData?.groups, groupRoles, oid, isAdminView]);

  useEffect(() => {
    const fetchUserPrograms = async () => {
      if (!userVisibleGroupIds) {
        setUserPrograms([]);
        setUserProgramsLoading(false);
        return;
      }

      const uniqueIds = Array.from(new Set(userVisibleGroupIds));
      const roleLabelsById = uniqueIds.reduce<Record<string, string[]>>((acc, id) => {
        const rolesForId = groupRoles
          .filter((each) => each.id === id && each.role !== 'admin')
          .map((each) => _.startCase(each.displayRole || each.role));
        acc[id] = rolesForId.length ? rolesForId : ['User'];
        return acc;
      }, {});

      // seed placeholders so UI can render skeleton per card
      const placeholders: UserProgramInfo[] = uniqueIds.map((id) => {
        const cached = allGroupData?.groups?.find((g) => g.grpId === id);
        return {
          id,
          name: cached?.name || 'Program',
          type: _.startCase(cached?.type || 'Program'),
          roles: roleLabelsById[id],
          status: cached?.status,
          loading: true
        };
      });

      setUserPrograms(placeholders);
      setUserProgramsLoading(false);

      const updateProgram = (id: string, updater: (prev: UserProgramInfo) => UserProgramInfo) => {
        setUserPrograms((prev) =>
          prev.map((p) => {
            if (p.id !== id) return p;
            return updater(p);
          })
        );
      };

      uniqueIds.forEach((id) => {
        const fetchProgram = async () => {
          const roles = roleLabelsById[id];
          const cached = allGroupData?.groups?.find((g) => g.grpId === id);
          let base: UserProgramInfo = {
            id,
            name: cached?.name || 'Program',
            type: _.startCase(cached?.type || 'Program'),
            roles: roles,
            progress: undefined,
            status: cached?.status,
            etaText: undefined,
            loading: true
          };

          try {
            const response = await API.getGroupDetails(id, undefined, true);
            const details = response?.data;
            base = {
              ...base,
              name: details?.name || base.name,
              type: _.startCase(details?.goals?.programType || details?.type || base.type),
              progress: details?.progress ?? details?.programProgress ?? base.progress,
              status: details?.status ?? base.status
            };
          } catch (error) {
            // fallback to cached/base
          }

          const hasMenteeRole = roles.some((role) => role.toLowerCase().includes('mentee'));
          const hasMentorRole = roles.some((role) => role.toLowerCase().includes('mentor'));

          if (hasMenteeRole) {
            try {
              const menteeProgressResp = await API.getMenteeProgress({ orgId, grpId: id });
              const menteeData = menteeProgressResp?.data?.data || menteeProgressResp?.data;
              const menteeProgressValue =
                menteeData?.progress ??
                menteeData?.progressPercentage ??
                menteeData?.progress_percent ??
                menteeData?.progressPerc ??
                menteeData?.programProgress;
              base = {
                ...base,
                progress: menteeProgressValue ?? base.progress,
                etaText: menteeData?.etaText ?? base.etaText,
                objectivesPending:
                  menteeData?.objectivesPending ?? menteeData?.objectivePending ?? menteeData?.objectivesRemaining
              };
            } catch (error) {
              // ignore per-card failure
            }
          }

          if (hasMentorRole) {
            try {
              const mentorStatsResp = await API.getMentorsPeersHomePage(orgId, location, id);
              const mentorData = mentorStatsResp?.data;
              base = {
                ...base,
                menteesEngaged:
                  mentorData?.connections ??
                  mentorData?.menteesEngaged ??
                  mentorData?.menteesCount ??
                  base.menteesEngaged,
                messagesSent: mentorData?.messagesSent ?? mentorData?.messages ?? base.messagesSent,
                meetingsScheduled: mentorData?.meetingsScheduled ?? mentorData?.meetings ?? base.meetingsScheduled
              };
            } catch (error) {
              // ignore per-card failure
            }
          }

          updateProgram(id, () => ({ ...base, loading: false }));
        };

        fetchProgram();
      });
    };

    fetchUserPrograms();
  }, [allGroupData?.groups, groupRoles, userVisibleGroupIds, orgId, location]);

  const filteredGroups = useMemo(() => {
    if (isAdminView) {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return adminPrograms;
      return adminPrograms.filter((program) => program.name.toLowerCase().includes(term));
    }

    const groups = allGroupData?.groups || [];
    const visibleGroups = userVisibleGroupIds
      ? groups.filter((group) => userVisibleGroupIds.includes(group.grpId))
      : groups;
    if (!searchTerm.trim()) return visibleGroups;
    const term = searchTerm.toLowerCase();
    return visibleGroups.filter((group) => group.name.toLowerCase().includes(term));
  }, [adminPrograms, allGroupData?.groups, isAdminView, searchTerm, userVisibleGroupIds]);

  const groupsByStatus = useMemo(() => {
    const buckets = STATUS_COLUMNS.reduce<Record<ColumnStatus, GroupInterface[]>>((acc, column) => {
      acc[column.key] = [];
      return acc;
    }, {} as Record<ColumnStatus, GroupInterface[]>);

    filteredGroups.forEach((group) => {
      const statusKey = getColumnStatus(group.status);
      buckets[statusKey].push(group);
    });

    return buckets;
  }, [filteredGroups]);

  const handleOpenSidebar = (p: GroupInterface) => {
    setSelectedProgram(p);
  };
  const handleCloseSidebar = () => {
    setSelectedProgram(undefined);
    setOpenSidebar(false);
  };
  const handleOpenUserDrawer = (program: UserProgramInfo) => {
    setSelectedUserProgram(program);
    setOpenUserDrawer(true);
  };
  const handleCloseUserDrawer = () => {
    setSelectedUserProgram(undefined);
    setOpenUserDrawer(false);
  };

  const renderSkeletonColumn = (title: string) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 300,
        height: '100%',
        background: '#1C1C1C',
        borderRadius: 2,
        border: '1px solid #2A2A2A',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400, fontFamily: 'Inter' }}>{title}</Typography>
        <Box sx={{ width: 16, height: 16, border: '1.5px solid #868686', borderRadius: 0.5 }} />
      </Box>

      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1, overflowY: 'auto' }}>
        {Array.from({ length: 3 }).map((_, idx) => (
          <MySkeleton key={idx} variant="rounded" height={160} />
        ))}
      </Box>
    </Box>
  );

  if (!isAdminView) {
    const buckets = userPrograms.reduce(
      (acc, program) => {
        const progress = typeof program.progress === 'number' ? program.progress : 0;
        const status = program.status?.toLowerCase();
        if (status?.includes('complete') || progress >= 100) {
          acc.completed.push(program);
        } else if (progress > 0 || status?.includes('progress')) {
          acc.inProgress.push(program);
        } else {
          acc.notStarted.push(program);
        }
        return acc;
      },
      { notStarted: [] as UserProgramInfo[], inProgress: [] as UserProgramInfo[], completed: [] as UserProgramInfo[] }
    );

    const renderColumn = (title: string, programs: UserProgramInfo[], dimCompleted?: boolean) => (
      <Box
        sx={{
          flex: 1,
          minWidth: 300,
          height: '100%',
          background: '#1C1C1C',
          borderRadius: 2,
          border: '1px solid #2A2A2A',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid #2A2A2A',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400, fontFamily: 'Inter' }}>{title}</Typography>
          <Box sx={{ width: 16, height: 16, border: '1.5px solid #868686', borderRadius: 0.5 }} />
        </Box>

        <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1, overflowY: 'auto' }}>
          {programs.length === 0 ? (
            <Typography sx={{ color: '#868686', fontSize: 12, fontFamily: 'Inter' }}>Nothing here yet.</Typography>
          ) : (
            programs.map((program) => (
              <Box key={program.id} sx={{ opacity: dimCompleted && !program.loading ? 0.4 : 1 }}>
                {program.loading ? (
                  <MySkeleton variant="rounded" height={160} />
                ) : (
                  <UserProgramCard program={program} />
                )}
              </Box>
            ))
          )}
        </Box>
      </Box>
    );

    return (
      <Box sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
        {userProgramsLoading ? (
          <Box sx={{ display: 'flex', gap: 2.5, height: '100%', overflow: 'auto' }}>
            {renderSkeletonColumn('Not Started')}
            {renderSkeletonColumn('In Progress')}
            {renderSkeletonColumn('Completed')}
          </Box>
        ) : userPrograms.length === 0 ? (
          <Typography sx={{ color: '#FDFDFD' }}>No programs available.</Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 2.5, height: '100%', overflow: 'auto' }}>
            {renderColumn(
              'Not Started',
              buckets.notStarted.map((program) => ({
                ...program,
                onClick: () => handleOpenUserDrawer(program)
              }))
            )}
            {renderColumn(
              'In Progress',
              buckets.inProgress.map((program) => ({
                ...program,
                onClick: () => handleOpenUserDrawer(program)
              }))
            )}
            {renderColumn(
              'Completed',
              buckets.completed.map((program) => ({
                ...program,
                onClick: () => handleOpenUserDrawer(program)
              })),
              true
            )}
          </Box>
        )}
        <UserProgramDrawer open={openUserDrawer} onClose={handleCloseUserDrawer} program={selectedUserProgram} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
      <DosenInput
        placeholder="Search programs"
        value={searchTerm}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
        fullWidth
        sx={{
          maxWidth: '100%',
          '& .MuiInputBase-root': {
            background: '#1C1C1C',
            borderRadius: 1,
            color: '#FDFDFD'
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#868686' }} />
            </InputAdornment>
          )
        }}
      />

      <Grid container columns={5} sx={{ overflow: 'hidden', flex: 1 }}>
        <Grid item xs={1}>
          <Box sx={{ background: '#333', height: '100%', borderRight: '1px solid #444444', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', overflowY: 'auto' }}>
              <ProgramsSidebar />
            </Box>
          </Box>
        </Grid>
        {STATUS_COLUMNS.map(({ key, label }, index) => (
          <Grid item xs={1} key={key} sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box
              sx={{
                background: '#2A2A2A',
                height: '100%',
                px: '10px',
                borderRight: '1px solid #444444',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}
            >
              <Box
                sx={{
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1
                }}
              >
                <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 500 }}>{label}</Typography>
                <InfoOutlinedIcon sx={{ color: '#868686', fontSize: 16 }} />
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', pr: 1, minHeight: 0 }}>
                {adminProgramsLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {Array.from({ length: 2 }).map((_, idx) => (
                      <MySkeleton key={idx} variant="rounded" height={160} />
                    ))}
                  </Box>
                ) : groupsByStatus[key].length === 0 ? (
                  filteredGroups.length === 0 && key === 'design' ? (
                    <Typography sx={{ color: '#B6B6B6', mt: 4, textAlign: 'center' }}>
                      {adminProgramsError || 'No programs found.'}
                    </Typography>
                  ) : null
                ) : (
                  groupsByStatus[key].map((group, groupIndex) => (
                    <React.Fragment key={group.grpId}>
                      <ProgramCard {...group} onClick={() => handleOpenSidebar(group)} />
                      {groupIndex !== groupsByStatus[key].length - 1 && <Box sx={{ height: '10px' }} />}
                    </React.Fragment>
                  ))
                )}
              </Box>
            </Box>
          </Grid>
        ))}
        <Sidebar
          open={openSidebar}
          onClose={handleCloseSidebar}
          program={selectedProgram}
          onManage={handleSidebarResume}
          canManageProgram={canManageSelectedProgram}
        />
      </Grid>
    </Box>
  );
};

const ProgramTable = () => {
  const allGroupData = useAppSelector((state) => state.allGroups.data);
  const grpId = useParams().id || '';
  const [addGrpLoader, setAddGrpLoader] = useState(false);
  const coId = useParams().coId || '';
  const [dialogType, setDialogType] = useState<string>('');
  // const orgGroupData = useAppSelector((state) => state.orgLevelGroup);
  // const allGroupsData = useAppSelector((state) => state.allGroups);
  // const grpList = useAppSelector((state) => state.groupRoleList);
  // const orgId = orgGroupData.data?.orgId || '';
  // const grpId2 = orgGroupData.data?.grpId || '';
  const dispatch = useAppDispatch();
  let requiredText = 'Program name is required';
  if (dialogType === 'level') {
    requiredText = 'Module name is required';
  }

  const schema = yup.object({
    name: yup
      .string()
      .required(requiredText)
      .test('no-whitespace', 'Min 3 characters without spaces', (value) => {
        if (!value) return false; // if value is undefined or null
        return value.replace(/\s/g, '').length >= 3;
      })
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(schema)
  });

  /** Filter */
  // const [openFilter, setopenFilter] = useState(false);
  // const handleopenFilter = (flag: boolean) => setopenFilter(flag);
  const [openAddProgram, setOpenAddProgram] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const openPopover = Boolean(anchorEl);
  const loading = useAppSelector((state) => state.allGroups.loading);
  const id = openPopover ? 'simple-popover' : undefined;
  const handleOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickOpenAddProgram = (type?: string) => {
    setOpenAddProgram(true);
    setDialogType(type || '');
  };

  const handleAddProgramDialog = (flag: boolean) => {
    setOpenAddProgram(flag);
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    setAddGrpLoader(true);
    try {
      let response;
      if (dialogType === 'level') {
        response = await API.createLevel({
          orgId: coId,
          parent: grpId,
          name: data.name
        });
      } else {
        response = await API.createGroup({
          orgId: 'org_uBHsHaMapIwKwxJI',
          parent: '652907a6aae01a41c10ebc96',
          name: data.name
        });
      }
      if (response.status === 200) {
        setAddGrpLoader(false);
        handleAddProgramDialog(false);
        if (dialogType === 'level') {
          dispatch(groupActions.addGroup(response.data.LevelDetails));
        } else {
          dispatch(groupActions.addGroup(response.data.groupDetails));
        }
        if (grpId) {
          dispatch(fetchAllGroupsList({ orgId: getUserDetails().orgId, groupId: grpId }));
        }
        // dispatch(fetchAllGroups({ orgId, grpId:grpId2 , useCache: false }));
        setValue('name', '');
        handleClose();
      }
    } catch (e) {
      toast.error('Could not create a group');
      setAddGrpLoader(false);
    }
  };

  const displayGroups = () => {
    return (
      <>
        {loading ? (
          <ProgramTableLoader />
        ) : (
          <>
            {allGroupData && allGroupData.groups ? (
              <Box mt={2}>
                <TableContainer>
                  <Table className="tableBody" width="100%" aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell colSpan={2} width="50%" align="left">
                          <Typography variant="h5">
                            {allGroupData.mainGroup ? 'All Programs' : 'Groups List'}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={3} width="50%">
                          <Box display="flex" justifyContent="flex-end" gap="5px">
                            {/* <FilterIcon size={34} onClick={() => handleopenFilter(true)} /> */}
                            {grpId !== 'admin' && <PlusCircleIcon size={34} onClick={(e) => handleOpen(e)} />}
                          </Box>
                          <Popover
                            id={id}
                            open={openPopover}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left'
                            }}
                            transformOrigin={{
                              vertical: 'center',
                              horizontal: 'right'
                            }}
                          >
                            <Box className="PopoverListStyle" onClick={() => handleClickOpenAddProgram('level')}>
                              <AddLevelIcon size={21} />
                              <Typography color="primary">Add a Level</Typography>
                            </Box>
                            <Box
                              className="PopoverListStyle"
                              onClick={() => handleClickOpenAddProgram(allGroupData.mainGroup ? 'programs' : 'groups')}
                            >
                              <CircleAddIcon size={21} />
                              <Typography color="primary">
                                Add a {allGroupData.mainGroup ? 'Program' : 'Group'}
                              </Typography>
                            </Box>
                          </Popover>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{allGroupData?.mainGroup ? 'PROGRAM' : 'GROUP'} NAME</TableCell>
                        <TableCell>ENROLLED</TableCell>
                        {/* <TableCell>ACTIVE</TableCell> */}
                        {/* <TableCell>ROI</TableCell> */}
                        {/* <TableCell>GOAL PROGRESS</TableCell> */}
                        {/* <TableCell>XP</TableCell> */}
                        {/* <TableCell>DAYS REMAINING</TableCell> */}
                        <TableCell>START DATE</TableCell>
                        {/* <TableCell>END DATE</TableCell> */}
                        <TableCell>STATUS</TableCell>
                        <TableCell>ACTION</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allGroupData.groups.length > 0 ? (
                        allGroupData.groups.map((item) => <ProgramTableRow data={item} key={item.grpId} />)
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography textAlign="center">No Groups Found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Typography textAlign="center" className="CardBox" mt={2} p={2}>
                {' '}
                Nothing to see here{' '}
              </Typography>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <AdminProgramDashboard />
      {/* Add a Program */}
      {/* {displayGroups()}  */}
      {/* <AddProgram
        handleAddProgramDialog={handleAddProgramDialog}
        addProgramDialogFlag={false}
        onSubmit={onSubmit}
        addGrpLoader={addGrpLoader}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        dialogType={dialogType}
        reset={reset}
      /> */}

      {/* Filter */}
      {/* <FilterModal
        loading={addGrpLoader}
        openFilter={openFilter}
        handleopenFilter={handleopenFilter}
      /> */}
    </>
  );
};

export default ProgramTable;
